import React, { useState, useEffect } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useAuth } from "@/context/AuthContext";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { ArrowLeft, User, Copy, Share2, Star, Users, Trophy, Heart, Loader2, MapPin } from "lucide-react";
import { getImageUrl } from "@/lib/images";
import { formatPrice } from "@/lib/utils";

export default function PlayerProfile() {
  const { playerId } = useParams();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { user } = useAuth();

  const [player, setPlayer] = useState(null);
  const [loading, setLoading] = useState(true);
  const [following, setFollowing] = useState(false);
  const [followLoading, setFollowLoading] = useState(false);
  const [followersCount, setFollowersCount] = useState(0);

  useEffect(() => {
    fetchPlayer();
  }, [playerId]);

  const fetchPlayer = async () => {
    setLoading(true);
    try {
      const r = await api.get(`/players/${playerId}`);
      setPlayer(r.data.player);
      setFollowing(r.data.player.is_following || false);
      setFollowersCount(r.data.player.followers_count || 0);
    } catch (e) {
      if (e.response?.status === 404) {
        setPlayer(null);
      } else {
        toast.error(formatApiError(e.response?.data?.detail));
      }
    } finally {
      setLoading(false);
    }
  };

  const handleFollow = async () => {
    if (!user) {
      toast.error(t("common.sign_in_to_book"));
      return;
    }
    setFollowLoading(true);
    try {
      if (following) {
        await api.delete(`/friends/${playerId}`);
        setFollowing(false);
        setFollowersCount((c) => Math.max(0, c - 1));
      } else {
        await api.post(`/friends/${playerId}`);
        setFollowing(true);
        setFollowersCount((c) => c + 1);
      }
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setFollowLoading(false);
    }
  };

  const handleCopyId = () => {
    navigator.clipboard.writeText(player.player_id);
    toast.success(t("common.copied"));
  };

  if (loading) {
    return (
      <div className="min-h-[calc(100vh-64px)] bg-background flex items-center justify-center">
        <div className="text-center">
          <div className="h-8 w-8 border-2 border-volt border-t-transparent animate-spin mx-auto mb-4" />
          <p className="text-muted-foreground text-sm">{t("common.loading")}</p>
        </div>
      </div>
    );
  }

  if (!player) {
    return (
      <div className="min-h-[calc(100vh-64px)] bg-background flex items-center justify-center">
        <div className="text-center">
          <User className="h-12 w-12 text-muted-foreground/40 mx-auto mb-4" />
          <p className="text-muted-foreground">Player not found</p>
          <button
            onClick={() => navigate(-1)}
            className="btn-outline mt-6 px-5 py-2 text-xs tracking-widest uppercase font-semibold"
          >
            {t("common.back")}
          </button>
        </div>
      </div>
    );
  }

  const ratings = player.ratings || {};
  const stats = player.stats || {};
  const teams = player.teams || [];
  const courts = player.courts || [];

  return (
    <div className="min-h-[calc(100vh-64px)] bg-background">
      <div className="max-w-[1200px] mx-auto px-5 md:px-10 py-10">
        {/* Back button */}
        <button
          onClick={() => navigate(-1)}
          className="btn-outline px-4 py-2 text-xs tracking-widest uppercase font-semibold flex items-center gap-2 mb-6"
        >
          <ArrowLeft className="h-3 w-3" /> {t("common.back")}
        </button>

        {/* Player header */}
        <div className="bg-card border border-border p-6 md:p-8 mb-8">
          <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-6">
            <div className="flex items-start gap-5 flex-1 min-w-0">
              {/* Avatar */}
              <div className="h-20 w-20 md:h-24 md:w-24 bg-volt/10 text-volt flex items-center justify-center shrink-0 rounded-full">
                {player.avatar ? (
                  <img
                    src={player.avatar}
                    alt={player.nickname || ""}
                    className="h-full w-full rounded-full object-cover"
                  />
                ) : (
                  <User className="h-8 w-8 md:h-10 md:w-10" />
                )}
              </div>

              {/* Info */}
              <div className="min-w-0">
                <h1 className="font-heading text-3xl md:text-5xl tracking-tight mb-1">
                  {player.nickname || "Player"}
                </h1>
                <div className="flex items-center gap-2 text-sm text-muted-foreground mb-3">
                  <span className="font-mono text-xs">#{player.player_id}</span>
                  <button
                    onClick={handleCopyId}
                    className="hover:text-volt transition-colors"
                    title={t("common.copy")}
                  >
                    <Copy className="h-3.5 w-3.5" />
                  </button>
                  <button
                    onClick={() => {
                      const url = `${window.location.origin}/player/${playerId}`;
                      if (navigator.share) {
                        navigator.share({ url });
                      } else {
                        navigator.clipboard.writeText(url);
                        toast.success(t("common.link_copied"));
                      }
                    }}
                    className="hover:text-volt transition-colors"
                    title={t("common.share")}
                  >
                    <Share2 className="h-3.5 w-3.5" />
                  </button>
                </div>
                {player.bio && (
                  <p className="text-sm text-muted-foreground/80 max-w-lg">{player.bio}</p>
                )}
                {player.favorite_sports && player.favorite_sports.length > 0 && (
                  <div className="mt-4">
                    <div className="label-eyebrow text-xs text-muted-foreground/80 mb-2">{t("player.favorite_sports")}</div>
                    <div className="flex flex-wrap gap-2">
                      {player.favorite_sports.map((sport) => (
                        <span key={sport} className="text-[10px] uppercase tracking-widest px-3 py-1 border border-border text-muted-foreground/80">
                          {t("home.sports." + sport)}
                        </span>
                      ))}
                    </div>
                  </div>
                )}
                {player.skill_rating != null && (
                  <div className="flex items-center gap-1 mt-3">
                    <Star className="h-4 w-4 fill-volt text-volt" />
                    <span className="font-semibold text-sm">{player.skill_rating}</span>
                    <span className="text-[10px] text-muted-foreground/60 ml-1">{t("profile.skill_avg")}</span>
                  </div>
                )}
              </div>
            </div>

            {/* Follow button + followers */}
            <div className="flex flex-col items-end gap-3 shrink-0">
              <div className="flex items-center gap-4 text-sm text-muted-foreground">
                <Heart className={`h-4 w-4 ${following ? "fill-red-400 text-red-400" : ""}`} />
                <span>{followersCount} {followersCount === 1 ? t("player.follower") : t("player.followers")}</span>
              </div>
              {user && user.id !== player.user_id && (
                <button
                  onClick={handleFollow}
                  disabled={followLoading}
                  className={following ? "btn-outline px-6 py-3 text-xs tracking-widest uppercase font-bold" : "btn-volt px-6 py-3 text-xs tracking-widest uppercase font-bold"}
                >
                  {followLoading ? (
                    <Loader2 className="h-4 w-4 animate-spin" />
                  ) : following ? (
                    "Unfollow"
                  ) : (
                    "Follow"
                  )}
                </button>
              )}
            </div>
          </div>
        </div>

        {/* Stats grid */}
        <div className="grid grid-cols-3 gap-4 mb-8">
          <div className="border border-border/30 bg-card p-5 text-center">
            <div className="font-heading text-3xl text-volt">{stats.matches_played || 0}</div>
            <div className="text-[10px] tracking-widest uppercase text-muted-foreground mt-1">Matches Played</div>
          </div>
          <div className="border border-border/30 bg-card p-5 text-center">
            <div className="font-heading text-3xl text-volt">{stats.wins || 0}</div>
            <div className="text-[10px] tracking-widest uppercase text-muted-foreground mt-1">Wins</div>
          </div>
          <div className="border border-border/30 bg-card p-5 text-center">
            <div className="font-heading text-3xl text-volt">{stats.losses || 0}</div>
            <div className="text-[10px] tracking-widest uppercase text-muted-foreground mt-1">Losses</div>
          </div>
        </div>

        {/* Average rating */}
        {ratings.count > 0 && (
          <div className="bg-card border border-border p-6 md:p-8 mb-8">
            <div className="flex items-center gap-2 mb-6">
              <Star className="h-5 w-5 fill-volt text-volt" />
              <h2 className="font-heading text-xl tracking-tight">{t("profile.ratings_title")}</h2>
            </div>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
              <div className="text-center">
                <div className="font-heading text-2xl text-volt">{ratings.overall_avg ?? "-"}</div>
                <div className="text-[10px] tracking-widest uppercase text-muted-foreground mt-1">{t("profile.overall_avg")}</div>
              </div>
              <div className="text-center">
                <div className="font-heading text-2xl text-volt">{ratings.punctuality_avg ?? "-"}</div>
                <div className="text-[10px] tracking-widest uppercase text-muted-foreground mt-1">{t("profile.punctuality_avg")}</div>
              </div>
              <div className="text-center">
                <div className="font-heading text-2xl text-volt">{ratings.skill_avg ?? "-"}</div>
                <div className="text-[10px] tracking-widest uppercase text-muted-foreground mt-1">{t("profile.skill_avg")}</div>
              </div>
              <div className="text-center">
                <div className="font-heading text-2xl text-volt">{ratings.attitude_avg ?? "-"}</div>
                <div className="text-[10px] tracking-widest uppercase text-muted-foreground mt-1">{t("profile.attitude_avg")}</div>
              </div>
            </div>
          </div>
        )}

        {/* Teams */}
        {teams.length > 0 && (
          <div className="bg-card border border-border p-6 md:p-8">
            <div className="flex items-center gap-2 mb-6">
              <Users className="h-5 w-5 text-volt" />
              <h2 className="font-heading text-xl tracking-tight">Teams</h2>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              {teams.map((team) => (
                <Link
                  key={team.id}
                  to={`/teams/${team.id}`}
                  className="bg-background border border-border px-4 py-3 flex items-center gap-3 hover:border-volt/50 transition-colors"
                >
                  <div className="h-10 w-10 bg-volt/10 text-volt flex items-center justify-center font-bold text-sm">
                    {(team.name || "?").slice(0, 1).toUpperCase()}
                  </div>
                  <div className="min-w-0">
                    <p className="text-sm font-semibold">{team.name}</p>
                    {team.sport && (
                      <p className="text-[10px] text-muted-foreground uppercase tracking-widest">{team.sport}</p>
                    )}
                  </div>
                </Link>
              ))}
            </div>
          </div>
        )}

        {/* Hosted Courts */}
        {courts.length > 0 && (
          <div className="bg-card border border-border p-6 md:p-8 mt-8">
            <div className="flex items-center gap-2 mb-6">
              <Trophy className="h-5 w-5 text-volt" />
              <h2 className="font-heading text-xl tracking-tight">Hosted Courts</h2>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {courts.map((court) => (
                <Link
                  key={court.id}
                  to={`/courts/${court.id}`}
                  className="bg-background border border-border overflow-hidden hover:border-volt/50 transition-colors group"
                >
                  <div className="aspect-[4/3] overflow-hidden">
                    <img
                      src={getImageUrl(court.images?.[0], 'card')}
                      alt={court.name}
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                    />
                  </div>
                  <div className="p-4">
                    <p className="font-heading text-lg tracking-tight group-hover:text-volt transition-colors">
                      {court.name}
                    </p>
                    <div className="flex items-center gap-2 mt-1 text-xs text-muted-foreground">
                      <span className="text-[10px] uppercase tracking-widest">
                        {court.sport}
                      </span>
                      <span>·</span>
                      <span className="flex items-center gap-1">
                        <MapPin className="h-3 w-3" />
                        {court.city}
                      </span>
                    </div>
                    <div className="flex items-center justify-between mt-4 pt-3 border-t border-border/30">
                      {court.rating > 0 && (
                        <div className="flex items-center gap-1 text-xs">
                          <Star className="h-3.5 w-3.5 fill-volt text-volt" />
                          <span className="font-semibold">{court.rating}</span>
                          {court.review_count > 0 && (
                            <span className="text-muted-foreground">({court.review_count})</span>
                          )}
                        </div>
                      )}
                      <span className="font-heading text-xl text-volt ml-auto">
                        {formatPrice(court.price_per_slot, court.currency)}
                      </span>
                    </div>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
